import React, {useEffect, useRef, useState} from 'react';
import {useLogin} from "../../api/auth/AuthService";
import {useLocation, useNavigate} from "react-router-dom";
import ITM from "../../resources/logo/logo_ITM.png"
import {useDispatch, useSelector} from "react-redux";
import {login, setPlatFrom} from "../../redux/slice/authSlice";
import {getMenusByUserGroup} from "../../api/menu/MenuService";
import styled from "styled-components";
import {ModalComputerRoomAccess} from "../../components/basic/ModalComputerRoomAccess";
import {ModalEquipmentAccess} from "../../components/basic/ModalEquipmentAccess";
import PlatformLogo from "../../layouts/PlatformLogo";
import {noAuh} from "../../api/instance/Instance";
import {getAdminMenuList} from "../../api/field/AdminMenuService";
import LoginBg from "../../resources/images/login_bg.png";
import {LOGIN_NOTICE_KEY} from "../../api/instance/serverUnavailable";

const InputBox = styled.input`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.25);
    height: 50px;
    color: #ffffff;
    font-weight: 500;
    font-size: 14px;
    padding: 0 48px 0 15px; /* 우측: Caps Lock 뱃지 여유 */
    background: rgba(255, 255, 255, 0.08);
    line-height: 24px;
    outline: none;
    border-radius: 6px;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    transition: border-color 0.2s ease, background 0.2s ease;

    &::placeholder {
        color: rgba(255, 255, 255, 0.55);
        font-weight: 400;
    }

    &:focus {
        border-color: rgba(255, 255, 255, 0.6);
        background: rgba(255, 255, 255, 0.14);
    }
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: 10px;
`;

const InputIndicators = styled.div`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 6px;
    pointer-events: none;
`;

const BadgeBase = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    padding: 4px 7px;
    border-radius: 4px;
    letter-spacing: 0.2px;
`;

const CapsBadge = styled(BadgeBase)`
    color: #fde68a;
    background: rgba(251, 191, 36, 0.22);
    border: 1px solid rgba(251, 191, 36, 0.55);
`;

const LoginProgressTrack = styled.div`
    width: 100%;
    height: 5px;
    overflow: hidden;
    border-radius: 9999px;
    background: rgba(148, 163, 184, 0.2);
`;

const LoginProgressBar = styled.div`
  height: 100%;
  width: 42%;
    border-radius: inherit;
    background: linear-gradient(90deg, #3b82f6 0%, #93c5fd 52%, #ffffff 100%);
    box-shadow: 0 0 16px rgba(96, 165, 250, 0.8);
    animation: loginProgress 1.15s ease-in-out infinite;

    @keyframes loginProgress {
        0% {
            transform: translateX(-115%);
        }
        100% {
            transform: translateX(275%);
        }
  }
`;

const AnimatedEllipsis = styled.span`
  display: inline-block;
  width: 1.5em;
  margin-left: 1px;
  text-align: left;

  &::after {
    content: "";
    animation: loadingEllipsis 1.2s steps(1, end) infinite;
  }

  @keyframes loadingEllipsis {
    0% {
      content: "";
    }
    25%,
    50% {
      content: ".";
    }
    75%,
    100% {
      content: "..";
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      content: "..";
      animation: none;
    }
  }
`;

export const StripeContainer = styled.div`
    margin-top:20px;
    height: 50px;
    width: 100%;
    overflow: hidden;
`;
export const ExpireButton = styled.div`
    text-align: center;
    line-height: 50px;
    font-weight: bold;
    font-size: 18px;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(
            45deg,
            rgb(239, 140, 140) 20%, /* 빨간색 (얇게) */ 
            rgba(255, 255, 255, 1) 20%, /* 흰색 시작 */ 
            rgba(255, 255, 255, 1) 50%, /* 흰색 끝 */ 
            rgba(239, 140, 140) 50%, /* 빨간색 시작 */ 
            rgba(239, 140, 140) 70%, /* 빨간색 (얇게) */ 
            rgba(255, 255, 255, 1) 70%); /* 흰색 시작 */
    background-size: 40px 40px;
    transition: width .10s ease;
    animation: progressAnimationStrike 5s linear infinite;

    &:hover {
        cursor: pointer;
    }
`;


function Authentication(props) {
    const platFrom = useSelector(state =>state.auth.platFrom)
    const [expired, setExpired] = useState(false);
    const [key, setKey] = useState(false);
    const [platFromReady, setPlatFromReady] = useState(false);
    useEffect( () => {
        const notice = sessionStorage.getItem(LOGIN_NOTICE_KEY);
        if (notice) {
            setServerNotice(notice);
            sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        }

        const applyLicenseHeaders = (headers = {}) => {
            setExpired(Boolean(headers['x-license']?.includes('EXPIRED')));
            setKey(Boolean(headers['x-license']?.includes('KEY')));
            const platform = headers['x-platform'];
            if (platform) {
                dispatch(setPlatFrom(platform.split(',')[0]));
            }
        };

        noAuh.get(`/`)
            .then((res) => {
                applyLicenseHeaders(res?.headers);
            })
            .catch(err => {
                if (!err.response) {
                    setExpired(false);
                    setKey(false);
                    setServerNotice((prev) => prev || "서버에 연결할 수 없습니다. 백엔드 서버가 실행 중인지 확인해 주세요.");
                    return;
                }
                applyLicenseHeaders(err.response.headers);
            })
            .finally(() => setPlatFromReady(true));
    },[])

    const navigate = useNavigate();
    const [username, setUsername]=useState("");
    const [password, setPassword]=useState("");
    const [message, setMessage]=useState("아이디를 입력하세요");
    const [serverNotice, setServerNotice] = useState("");
    const [moreThanThree, setMoreThanThree]=useState(false);
    const [nameInputMode, setNameInputMode] =useState(true);
    // Caps Lock — 비밀번호 필드 전용
    const [capsLockOn, setCapsLockOn] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [loginMessage, setLoginMessage] = useState("");
    const dispatch = useDispatch();
    const location = useLocation();

    const detectCapsLock = (e) => {
        const nativeEvent = e.nativeEvent || e;
        if (typeof nativeEvent.getModifierState === "function") {
            setCapsLockOn(nativeEvent.getModifierState("CapsLock"));
        }
    };


    const typeUsername = (e) => {
        const newUsername = e.target.value;
        setUsername(e.target.value)
        if(newUsername.length > 3){
            setMessage("")
            setMoreThanThree(true)
            setMessage("로그인 하세요")
        }else{
            setMoreThanThree(false)
            setMessage("아이디를 입력하세요")
        }
    }

    const typePassword = (e) => {

        setPassword(e.target.value)
    }
    const focusRef =useRef(null);




    const passwordEnter = async (e) => {
            if (e.key === "Enter" || e.type === "click") {
                const loginUser = {
                    "username": username,
                    "password": password
                }
                if (!expired) {
                    setIsLoggingIn(true);
                    setLoginMessage("로그인 정보를 확인하고 있습니다.");
                    try {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const user = await useLogin(loginUser);
                        setLoginMessage("사용자 메뉴를 불러오고 있습니다");
                        const response = await getMenusByUserGroup();
                        if (response && response.status === 200) {
                            localStorage.setItem('menus', JSON.stringify(response.data));
                            sessionStorage.setItem("initTopMenu", "1");
                            // navigate(location.pathname && location.pathname ==='/login'? '/' : location.pathname)
                        }

                        setLoginMessage("사용자 환경을 구성하고 있습니다.");
                        const data = await getAdminMenuList();
                        if(data.status === 200){
                            const filteredData = data.data.filter(each => each.storage === true);
                            localStorage.setItem("setting", JSON.stringify(filteredData));
                        }

                        dispatch(login(user));
                    } catch (error) {
                        console.error(error);
                        setPassword("");
                    } finally {
                        setIsLoggingIn(false);
                        setLoginMessage("");
                    }
                } else if (expired && !key) {
                    alert("고객사의 임대 라이선스 기간이 만료되었습니다\n라이선스 갱신을 위해 담당자에게 문의 바랍니다.\n(심스소프트 02-2633-7040)")
                } else if (key) {
                    alert("라이선스 오류입니다.\n라이선스 갱신을 위해 담당자에게 문의 바랍니다.\n(심스소프트 02-2633-7040)")
                }
            }
    }


    const backButtonHandler = () => {
        setPassword("");
        setNameInputMode(true);
    }
    const homeButtonHandler = () => {
        setUsername("");
        setPassword("");
        navigate('/');
    }

    return (
        <div
            className="relative min-h-screen w-full overflow-y-auto md:overflow-hidden bg-[#0b1220]"
            style={{
                backgroundImage: `url(${LoginBg})`,
                // backgroundImage:
                //     "linear-gradient(135deg, #0b1220 0%, #111c2e 45%, #0b1220 100%)",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            {/* 어두운 오버레이 (추후 배경 이미지 추가 시 가독성 확보용) */}
            <div className="absolute inset-0 bg-black/30" />

            {/* 좌우 분할 컨테이너 */}
            <div className="relative z-10 flex min-h-screen items-start md:items-center justify-center md:justify-between px-[8%] gap-[40px] py-16 md:py-0">
                {/* 왼쪽: 솔루션 타이틀 */}
                <div className="hidden md:block text-white">
                    <p className="text-[36px] xl:text-[44px] font-bold leading-[1.25] tracking-tight">
                        {platFrom === "ITM"
                            ? "IT 통합 유지관리 솔루션"
                            : "IAM" === platFrom
                                ? "IT 자산관리 솔루션"
                                : "서비스 요청관리 솔루션"}
                    </p>
                    <p className="text-[36px] xl:text-[44px] font-bold leading-[1.25] tracking-tight mt-[4px]">
                        SIMMS-{platFrom || "ITM"} V3
                    </p>
                </div>

                {/* 오른쪽: 로그인 카드 (글래스모피즘)
                    - backdrop-filter를 카드 자체에 적용하면 내부 fixed 모달이 카드 기준으로 위치되므로
                    - 별도의 absolute 자식 레이어로 분리하여 형제로 둠 (모달의 fixed가 viewport 기준 유지) */}
                <div
                    className="relative w-[400px] max-w-full rounded-[14px] shadow-2xl border login-container overflow-hidden"
                    style={{
                        borderColor: "rgba(255, 255, 255, 0.18)",
                    }}
                >
                    {/* 블러/반투명 배경 레이어 (시각 효과 전용, 인터랙션 차단 X) */}
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: "rgba(15, 23, 42, 0.55)",
                            backdropFilter: "blur(18px)",
                            WebkitBackdropFilter: "blur(18px)",
                        }}
                    />

                    <div className="relative z-10 pl-[28px] pr-[28px] pt-[32px] pb-[24px]">
                        {/* 카드 내 로고 */}
                        <div className="flex justify-center items-center mb-[26px] min-h-[48px]">
                            {platFromReady && <PlatformLogo className={"w-[200px]"} />}
                        </div>

                        {serverNotice &&
                            <div
                                className="mb-4 rounded-md border border-amber-400/50 bg-amber-500/15 px-3 py-2 text-[13px] leading-relaxed text-amber-100"
                                role="alert"
                            >
                                {serverNotice}
                            </div>
                        }

                        <div>
                            <InputWrapper>
                                <InputBox type="text" placeholder="아이디"
                                          value={username}
                                          autoFocus
                                          onChange={typeUsername}
                                />
                            </InputWrapper>

                            <InputWrapper>
                                <InputBox type="password" placeholder="비밀번호"
                                          value={password}
                                          ref={focusRef}
                                          onChange={typePassword}
                                          onKeyPress={passwordEnter}
                                          onKeyDown={detectCapsLock}
                                          onKeyUp={detectCapsLock}
                                />
                                <InputIndicators>
                                    {capsLockOn && (
                                        <CapsBadge aria-label="Caps Lock 켜짐">
                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                 viewBox="0 0 24 24"
                                                 fill="none"
                                                 stroke="currentColor"
                                                 strokeWidth="2.5"
                                                 strokeLinecap="round"
                                                 strokeLinejoin="round"
                                                 width="14" height="14">
                                                <path d="M12 5l7 8h-4v6h-6v-6H5z"/>
                                            </svg>
                                        </CapsBadge>
                                    )}
                                </InputIndicators>
                            </InputWrapper>
                        </div>
                        <div className="mt-[14px]">
                            {!expired && !key &&
                                <button
                                    className="h-[50px] w-[100%] pl-[20px] pr-[20px] bg-[#3b82f6] hover:bg-[#2563eb] transition-colors duration-150 rounded-[6px] text-[16px] text-center text-white font-semibold shadow-md"
                                    onClick={passwordEnter}>
                                    로그인
                                </button>
                            }
                            {expired && !key &&
                                <StripeContainer>
                                    <ExpireButton
                                        onClick={() => alert("고객사의 임대 라이선스 기간이 만료되었습니다\n라이선스 갱신을 위해 담당자에게 문의 바랍니다.\n(심스소프트 02-2633-7040)")}>
                                        라이선스 기간 만료 안내
                                    </ExpireButton>
                                </StripeContainer>
                            }

                            {key &&
                                <StripeContainer>
                                    <ExpireButton
                                        onClick={() => alert("라이선스 오류입니다.\n라이선스 갱신을 위해 담당자에게 문의 바랍니다.\n(심스소프트 02-2633-7040)")}>
                                        라이선스 오류
                                    </ExpireButton>
                                </StripeContainer>
                            }
                        </div>

                        {/* 하단 패널 영역 - 각 모달 컴포넌트에서 렌더링 */}
                        {platFrom !== "SRM" &&
                            <div className="mt-[22px] mb-[6px] pt-[18px] border-t border-white/15 px-4">
                                <div className="flex justify-between gap-[14px] auth-bottom-actions">
                                    <ModalComputerRoomAccess />
                                    <ModalEquipmentAccess />
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

            {isLoggingIn && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#020617]/70 px-5 backdrop-blur-[6px]"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="login-loading-title"
                    aria-describedby="login-loading-message"
                >
                    <div className="relative w-full max-w-[420px] overflow-hidden rounded-[18px] border border-white/15 bg-[#0f172a]/95 px-8 py-9 text-center shadow-[0_28px_80px_rgba(0,0,0,0.55)]">
                        <div
                            aria-hidden="true"
                            className="absolute left-1/2 top-0 h-[110px] w-[260px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/20 blur-[45px]"
                        />
                        <div className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center">
                            <span className="absolute inset-0 animate-ping rounded-full border border-blue-300/35" />
                            <span className="absolute inset-[7px] animate-pulse rounded-full bg-blue-500/20" />
                            <svg
                                className="relative h-7 w-7 animate-spin text-blue-300"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="9"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                />
                                <path
                                    className="opacity-90"
                                    fill="currentColor"
                                    d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3Z"
                                />
                            </svg>
                        </div>
                        <h2
                            id="login-loading-title"
                            className="text-[20px] font-semibold tracking-[-0.02em] text-white"
                        >
                            로그인 처리 중
                        </h2>
                        <p
                            id="login-loading-message"
                            className="mt-2 min-h-[21px] text-[14px] text-slate-300"
                            aria-live="polite"
                        >
                            {loginMessage}
                            <AnimatedEllipsis aria-hidden="true" />
                        </p>
                        <p className="mt-4 text-[12px] text-slate-500">
                            잠시만 기다려 주세요. 안전하게 로그인하고 있습니다.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Authentication;
